import { useForm, type Control } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import {
  osImageFormSchema,
  type OSImageFormData,
} from "@/schemas/adminOSImage.schema";
import {
  adminOSImageService,
  toFormValues,
} from "@/services/admin-os-image.service";
import { NOTICE_ALLOWED_TAGS } from "@/lib/noticeHtml";
import type { AdminOSImage, OSCategory } from "@/types/admin";
import { OSImagePreview } from "./OSImagePreview";

/**
 * The form element's id. The Save button lives in the page header, outside this component's DOM,
 * and submits through `<button type="submit" form={OS_IMAGE_FORM_ID}>`.
 */
export const OS_IMAGE_FORM_ID = "os-image-form";

interface OSImageFormProps {
  image?: AdminOSImage;
  onSubmit: (data: OSImageFormData) => void;
}

/** Switch tile shared by every on/off setting, so the tiles line up at one height. */
const SWITCH_TILE_CLASS =
  "flex h-full flex-row items-center justify-between gap-4 space-y-0 rounded-lg border px-4 py-3";

/**
 * OS image create/edit form.
 *
 * Laid out like the other admin form pages (ProvisionVmForUserPage): one card per concern on the
 * left, every row filled in pairs so no field sits beside an empty half, and the client preview
 * pinned on the right. The page renders the header, including Cancel and Save.
 */
export function OSImageForm({ image, onSubmit }: OSImageFormProps) {
  const [isVerifyingTemplate, setIsVerifyingTemplate] = useState(false);
  const [templateVerified, setTemplateVerified] = useState<boolean | null>(
    null,
  );

  const form = useForm<OSImageFormData>({
    // @ts-expect-error - zodResolver type inference issue with optional fields from .default()
    resolver: zodResolver(osImageFormSchema),
    defaultValues: image
      ? toFormValues(image)
      : {
          name: "",
          version: "",
          category: "Linux",
          description: "",
          iconUrl: "",
          templateName: "",
          minimumSpecs: {
            cpu: 1,
            memory: 1024,
            disk: 20,
          },
          isActive: true,
          isFeatured: false,
          licenseActivationSupported: true,
          licenseWarningMessage: "",
        },
  });

  const watchedValues = form.watch();
  const templateName = form.watch("templateName");

  // Verify VM template when template name changes
  useEffect(() => {
    const verifyTemplate = async () => {
      if (!templateName || templateName.length < 3) {
        setTemplateVerified(null);
        return;
      }

      setIsVerifyingTemplate(true);
      setTemplateVerified(null);

      try {
        const isVerified =
          await adminOSImageService.verifyTemplate(templateName);
        setTemplateVerified(isVerified);
        if (!isVerified) {
          form.setError("templateName", {
            type: "manual",
            message: "Template not found in WiService",
          });
        } else {
          form.clearErrors("templateName");
        }
      } catch (error) {
        console.error("Error verifying template:", error);
        setTemplateVerified(false);
        form.setError("templateName", {
          type: "manual",
          message: "Failed to verify template",
        });
      } finally {
        setIsVerifyingTemplate(false);
      }
    };

    const debounceTimer = setTimeout(verifyTemplate, 500);
    return () => clearTimeout(debounceTimer);
  }, [templateName, form]);

  // Catalog categories as stored on the row — the customer catalog groups by these exact values.
  const categories: OSCategory[] = ["Linux", "Windows", "BSD", "Custom"];

  const handleFormSubmit = (data: OSImageFormData) => {
    // The Save button is in the page header and cannot see this component's state, so the guard
    // that used to disable it lives here: a save while the template check is still in flight would
    // store a name WiService may not know.
    if (isVerifyingTemplate) {
      return;
    }

    onSubmit(data);
  };

  // Type assertion to fix FormField control type inference issue
  const typedControl = form.control as unknown as Control<OSImageFormData>;

  return (
    <Form {...form}>
      <form
        id={OS_IMAGE_FORM_ID}
        // @ts-expect-error - Form component type inference issue with react-hook-form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem] xl:grid-cols-[minmax(0,1fr)_24rem]"
      >
        <div className="min-w-0 space-y-4">
          {/* Basic Information */}
          <Card className="shadow-none">
            <CardHeader className="px-4 pb-3 pt-4">
              <CardTitle className="text-base">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 px-4 pb-4 pt-0 sm:grid-cols-2">
              <FormField
                control={typedControl}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Ubuntu Server" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={typedControl}
                name="version"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Version</FormLabel>
                    <FormControl>
                      <Input placeholder="22.04 LTS" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={typedControl}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Sort Order used to share this row; it was removed (the catalog is ordered by
                  name), and Icon URL moved up so the row stays paired. */}
              <FormField
                control={typedControl}
                name="iconUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Icon URL</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://example.com/icon.svg"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={typedControl}
                name="description"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe the OS image features and use cases..."
                        rows={4}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Technical Settings */}
          <Card className="shadow-none">
            <CardHeader className="px-4 pb-3 pt-4">
              <CardTitle className="text-base">Technical Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 px-4 pb-4 pt-0">
              <FormField
                control={typedControl}
                name="templateName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>VM Template Name</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          placeholder="Ubuntu-24.04"
                          className="pr-9"
                          {...field}
                        />
                        {isVerifyingTemplate && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          </div>
                        )}
                        {!isVerifyingTemplate && templateVerified === true && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <CheckCircle2 className="h-4 w-4 text-ok-ink" />
                          </div>
                        )}
                        {!isVerifyingTemplate &&
                          templateVerified === false && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <XCircle className="h-4 w-4 text-destructive-ink" />
                            </div>
                          )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 sm:grid-cols-3">
                <FormField
                  control={typedControl}
                  name="minimumSpecs.cpu"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Min. CPU Cores</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          {...field}
                          onChange={(e) =>
                            field.onChange(parseInt(e.target.value) || 1)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={typedControl}
                  name="minimumSpecs.memory"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Min. Memory (MB)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="512"
                          {...field}
                          onChange={(e) =>
                            field.onChange(parseInt(e.target.value) || 512)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={typedControl}
                  name="minimumSpecs.disk"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Min. Disk (GB)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="10"
                          {...field}
                          onChange={(e) =>
                            field.onChange(parseInt(e.target.value) || 10)
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Availability */}
          <Card className="shadow-none">
            <CardHeader className="px-4 pb-3 pt-4">
              <CardTitle className="text-base">Availability</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 px-4 pb-4 pt-0 sm:grid-cols-2">
              <FormField
                control={typedControl}
                name="isActive"
                render={({ field }) => (
                  <FormItem className={SWITCH_TILE_CLASS}>
                    <FormLabel className="text-sm">Active</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={typedControl}
                name="isFeatured"
                render={({ field }) => (
                  <FormItem className={SWITCH_TILE_CLASS}>
                    <FormLabel className="text-sm">Featured</FormLabel>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Windows License — the message is written here and shown in the customer VM wizard
              when a Windows image cannot activate a license. */}
          {watchedValues.category === "Windows" && (
            <Card className="shadow-none">
              <CardHeader className="px-4 pb-3 pt-4">
                <CardTitle className="text-base">Windows License</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 px-4 pb-4 pt-0">
                <FormField
                  control={typedControl}
                  name="licenseActivationSupported"
                  render={({ field }) => (
                    <FormItem className={SWITCH_TILE_CLASS}>
                      <FormLabel className="text-sm">
                        Supports license activation
                      </FormLabel>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {!watchedValues.licenseActivationSupported && (
                  <FormField
                    control={typedControl}
                    name="licenseWarningMessage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Warning message</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="<p>This image cannot activate a <strong>Windows license</strong>.</p>"
                            rows={6}
                            maxLength={4000}
                            spellCheck={false}
                            className="font-mono text-xs"
                            {...field}
                          />
                        </FormControl>
                        {/* The one note kept on this form: without it nobody knows HTML works,
                            or which tags survive the sanitiser. */}
                        <FormDescription className="flex flex-wrap items-center gap-1">
                          <span>HTML allowed:</span>
                          {NOTICE_ALLOWED_TAGS.map((tag) => (
                            <code
                              key={tag}
                              className="rounded bg-muted px-1 font-mono text-[11px]"
                            >
                              &lt;{tag}&gt;
                            </code>
                          ))}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Preview Column */}
        <aside className="min-w-0 lg:sticky lg:top-0">
          <OSImagePreview formData={watchedValues} />
        </aside>
      </form>
    </Form>
  );
}
